import styled, { keyframes } from "styled-components";

const animateText = keyframes`
	0% {
		background-position: 0% 50%;
	}
	50% {
		background-position: 100% 50%;
	}
	100% {
		background-position: 0% 50%;
	}
`;

const twinklePixel = keyframes`
  0%, 100% {
    opacity: 0.45;
  }
  50% {
    opacity: 1;
  }
`;

const shimmerPixel = keyframes`
  0%, 100% {
    opacity: 0.18;
    transform: translateX(-32px);
  }
  50% {
    opacity: 0.55;
    transform: translateX(32px);
  }
`;

export const IndexStyle = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  @media (max-width: 900px) {
    min-height:unset;
  }
  .main-wrapper {
    position: relative;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100vh;
    height: 100dvh;
    max-height: 100vh;
    max-height: 100dvh;
    overflow: hidden;
    .theme-bg {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      z-index: 0;
      overflow: hidden;
      pointer-events: none;
      background-repeat: no-repeat;
      background-size: cover;
      background-position: center;
      img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
      }
      &::after {
        content: "";
        position: absolute;
        inset: 0;
        background:
          linear-gradient(
            to bottom,
            rgba(0, 0, 0, 0.18),
            transparent 35%,
            rgba(0, 0, 0, 0.2)
          ),
          radial-gradient(
            circle at 50% 38%,
            rgba(255, 255, 255, 0.12),
            transparent 34%
          );
        pointer-events: none;
      }
      svg {
        display: block;
        width: 100%;
        height: 100%;
        overflow: hidden;
      }
      .pixel-star,
      .pixel-twinkle {
        animation: ${twinklePixel} 4.8s ease-in-out infinite;
      }
      .pixel-shimmer {
        animation: ${shimmerPixel} 8s ease-in-out infinite;
      }
    }
  }
  .main {
    position: relative;
    display: grid;
    gap: 50px;
    grid-template-columns: 1fr 1fr;
    align-items: center;
    justify-content: center;
    min-height: 0;
    padding: 100px 20px 20px 20px;
    flex: 1 1 auto;
    z-index: 1;
    &.night {
      .right-frame {
        .info-icon {
          background: rgba(35, 213, 171, 0.22) !important;
        }
      }
    }
    .weather {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      z-index: 0;
      --top-color: #fff;
      background: linear-gradient(to bottom, var(--top-color), transparent);
      transition: opacity 1s ease-in-out;
      &.show {
        opacity: 1;
      }
      &.night {
        --top-color: #000000ff;
        .stars {
          position: absolute;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
          overflow: hidden;
          z-index: 0;


          .star {
            position: absolute;
            border-radius: 50%;
            animation-name: twinkle;
            animation-timing-function: ease-in-out;
            animation-iteration-count: infinite;
          }
        }

        @keyframes twinkle {
          0%,
          100% {
            opacity: 0.2;
            filter: brightness(1);
          }
          50% {
            opacity: 1;
            filter: brightness(1.8);
          }
        }
      }
      &.bright {
        --top-color: #83daf0;
      }
      &.sunny {
        --top-color: #f8e79a;
      }
      &.cloudy {
        --top-color: #c0c0c0;
      }
      &.rainy,
      &.heavy-rain,
      &.snow {
        --top-color: #305472;
        overflow: hidden;
        .rain {
          position: absolute;
          width: 120vw;
          height: 100vh;
          left: -10vw;
          top: 0;
          cursor: pointer;
          z-index: 100;
          @property --angle {
            syntax: "<angle>";
            inherits: false;
            initial-value: 91deg;
          }


          .drop {
            border: 0.35vmin solid transparent;
            border-bottom-color: #abc2e9;
            position: absolute;
            top: -5vmin;
            --angle: 91deg;
            animation-name: fall;
            animation-timing-function: ease-in;
            animation-iteration-count: infinite;
          }

          @keyframes fall {
            0% {
              transform: rotate(var(--angle)) translateX(0);
            }
            100% {
              transform: rotate(var(--angle)) translateX(calc(100vh + 5vmin));
            }
          }
        }
        &.snow {
          --top-color: #a4a2a2ff;
          .drop {
            position: absolute;
            top: -5vmin;
            width: 0.5vmin;
            height: 0.5vmin;
            background: white;
            border-radius: 50%;
            opacity: 0.8;
            filter: blur(0.5px);
            animation-name: fall;
            animation-timing-function: linear;
            animation-iteration-count: infinite;
            animation-duration: 5s;
          }
        }
      }
    }
    .title-wrapper {
      position: relative;
      display: flex;
      flex-direction: column;
      .title-colors {
        font-weight: bold;
        background-image: linear-gradient(
          -45deg,
          #ee7752,
          #e73c7e,
          #23a6d5,
          #23d5ab
        );
        background-clip: text;
        -webkit-background-clip: text;
        color: transparent;
        background-size: 200% 100%;
        animation: ${animateText} 3s ease infinite;
      }
      .title-name {
        display: flex;
        justify-content: center;
        flex-wrap: nowrap;
        .title-english {
          .title-english-1,
          .title-english-2 {
            color: #fff !important;
            font-size: 7rem;
            line-height: 5.5rem;
            font-weight: 900;
            color: var(--h1-light);
            &.title-english-2 {
              font-size: 7.5rem;
              line-height: 6rem;
            }
          }
        }
        .title-japanese {
          font-size: 3.8rem;
          line-height: 3.8rem;
          writing-mode: vertical-rl;
          text-orientation: upright;
        }
      }
      .title-icon-list {
        width: 100%;
        max-width: 515px;
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        justify-content: space-between;
        gap: 5px;
        margin: 10px auto 0 auto;
        li {
          img {
            width: 2.2rem;
          }
        }
      }
      .title-frontend-wrapper {
        min-width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 5px;
        .title-frontend {
          text-align: center;
          line-height: 3.2rem;
          white-space: nowrap;
          flex: 1 1 auto;
          flex-shrink: 1;
          min-width: 0;
          font-size: clamp(0.8rem, 10vw, 3.16rem);
          font-family: inherit;
          font-weight: 900;
          overflow: hidden;
        }
      }
    }
  }
  .right-frame {
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1;
    width: 100%;
    padding: 0 10px;

    .info-card {
      width: 100%;
      max-width: 440px;
      background: rgba(255, 255, 255, 0.55);
      backdrop-filter: blur(14px);
      -webkit-backdrop-filter: blur(14px);
      border: 1px solid rgba(255, 255, 255, 0.65);
      border-radius: 18px;
      padding: 22px 24px;
      box-shadow: 0 16px 34px -18px rgba(20, 40, 70, 0.35);
    }

    .info-table {
      display: flex;
      flex-direction: column;
      margin: 0;
    }

    .info-row {
      display: grid;
      grid-template-columns: auto 1fr;
      align-items: start;
      gap: 4px 14px;
      padding: 10px 0;
      border-bottom: 1px solid rgba(20, 40, 70, 0.09);

      &:last-child {
        border-bottom: none;
        padding-bottom: 4px;
      }
      &:first-child {
        padding-top: 0;
      }

      dt {
        display: flex;
        align-items: center;
        gap: 10px;
        margin: 0;
        font-weight: 600;
        font-size: 0.82rem;
        letter-spacing: 0.01em;
        white-space: nowrap;
        color: #7c2702;
      }
      dd {
        margin: 0;
        font-weight: 500;
        font-size: 0.92rem;
        text-align: right;
        justify-self: end;
      }
      .info-icon {
        background: rgba(238, 119, 82, 0.16);
      }
    }

    .info-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 26px;
      height: 26px;
      border-radius: 8px;
      font-size: 0.85rem;
      line-height: 1;
    }

    .languages {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      justify-content: flex-start;

      span {
        display: inline-flex;
        align-items: center;
        flex-wrap: nowrap;
        gap: 5px;
        background: rgba(20, 40, 70, 0.06);
        padding: 3px 9px 3px 6px;
        border-radius: 999px;
        font-size: 0.78rem;
        font-weight: 600;
        color: inherit;
        white-space: nowrap;
        img {
          width: 14px;
          height: 14px;
          border-radius: 2px;
          flex-shrink: 0;
        }
      }
    }

    .documents {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin-top: 16px;
      padding-top: 16px;
      border-top: 1px dashed rgba(20, 40, 70, 0.18);
    }

    .document-item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 9px 12px;
      border-radius: 10px;
      background: rgba(255, 255, 255, 0.65);
      border: 1px solid rgba(20, 40, 70, 0.08);
      font-size: 0.84rem;
      font-weight: 600;
      color: inherit;
      transition:
        transform 0.15s ease,
        box-shadow 0.15s ease,
        background 0.15s ease;

      img {
        width: 18px;
        height: 18px;
        flex-shrink: 0;
      }

      .doc-arrow {
        margin-left: auto;
        width: 14px;
        height: 14px;
        opacity: 0;
        transform: translateX(-4px);
        transition:
          opacity 0.15s ease,
          transform 0.15s ease;
      }

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 20px -10px rgba(20, 40, 70, 0.35);
        background: rgba(255, 255, 255, 0.95);

        .doc-arrow {
          opacity: 1;
          transform: translateX(0);
        }
      }
    }
  }

  &.theme-nordic {
    .theme-bg {
      --sky-top: #bcd8e8;
      --sky-bottom: #f5e1c8;
      --mtn-far: #8fb4c9;
      --mtn-mid: #6a94ab;
      --mtn-near: #3c5f7d;
      --mtn-highlight: #b9d4df;
      --snow: #ffffff;
      --fjord: #6aa8bd;
      --fjord-shimmer: #d8f7ff;
      --forest-far: #527c55;
      --tree-near: #284f35;
      --trunk: #5b3a29;
      --cabin: #a83e3e;
      --roof: #5a2c2c;
      --door: #3a2418;
      --window: #ffd873;
      --chimney: #3b2630;
      --cloud: #edf8ff;
      --star: #fff7c8;
      --particle: #ffffff;
      --aurora-a: #60f0bf;
      --aurora-b: #73a7ff;
      --aurora-c: #e1fff5;
      --aurora-opacity: 0;
    }
    .title-colors {
      background-image: linear-gradient(
        -45deg,
        #2de2c9,
        #4f8ff7,
        #7c5cff,
        #23d5ab
      );
    }
    .title-japanese {
      font-family: Georgia, "Iowan Old Style", "Palatino Linotype", serif;
    }
  }
  &.theme-japan {
    .theme-bg {
      --sky-top: #ffd0df;
      --sky-bottom: #8ec9e8;
      --moon: #fff3be;
      --star: #fff7d8;
      --cloud: #fff3f7;
      --fuji: #5a7fa3;
      --fuji-shadow: #3f6286;
      --snow: #ffffff;
      --ground: #527758;
      --village-far: #426363;
      --roof: #8f2f47;
      --building: #f3d0a9;
      --torii: #d83f35;
      --lantern: #ffcf62;
      --neon: #35e5ff;
      --bamboo: #315c35;
      --trunk: #6d3f2f;
      --sakura: #ff9fc2;
      --sakura-dark: #d76f97;
    }
    .title-japanese {
      font-family: Georgia, "Iowan Old Style", "Palatino Linotype", serif;
    }
  }
  &.weather-bright.theme-nordic,
  &.weather-sunny.theme-nordic {
    .theme-bg {
      --sky-top: #9fd8f0;
      --sky-bottom: #ffe4a8;
      --mtn-far: #91bfd0;
      --mtn-mid: #6f9db2;
      --mtn-near: #416b86;
      --mtn-highlight: #d0e6ec;
      --fjord: #5fb5c9;
      --fjord-shimmer: #f0ffff;
      --cloud: #fff8e6;
      --star: transparent;
      --window: #ffe89b;
      --aurora-opacity: 0;
    }
  }
  &.weather-cloudy.theme-nordic {
    .theme-bg {
      --sky-top: #9fb4bf;
      --sky-bottom: #cbd0c4;
      --mtn-far: #7895a2;
      --mtn-mid: #627f8d;
      --mtn-near: #3e5968;
      --mtn-highlight: #aebec5;
      --fjord: #5f8894;
      --fjord-shimmer: #bcd6db;
      --forest-far: #4e684f;
      --tree-near: #2f4935;
      --cloud: #dbe1df;
      --star: transparent;
      --aurora-opacity: 0;
    }
  }
  &.weather-rainy.theme-nordic,
  &.weather-heavy-rain.theme-nordic {
    .theme-bg {
      --sky-top: #4f6572;
      --sky-bottom: #7c8d91;
      --mtn-far: #536f7b;
      --mtn-mid: #435f6d;
      --mtn-near: #273f4d;
      --mtn-highlight: #718793;
      --fjord: #3f6572;
      --fjord-shimmer: #84aeb8;
      --forest-far: #38503e;
      --tree-near: #20382b;
      --cloud: #8f9da1;
      --window: #ffd36e;
      --star: transparent;
      --aurora-opacity: 0;
    }
  }
  &.weather-snow.theme-nordic {
    .theme-bg {
      --sky-top: #c4d8e3;
      --sky-bottom: #eef0e8;
      --mtn-far: #a0bdca;
      --mtn-mid: #7fa1b0;
      --mtn-near: #557083;
      --mtn-highlight: #dbeaf0;
      --snow: #ffffff;
      --fjord: #82aeb9;
      --fjord-shimmer: #eefcff;
      --forest-far: #5f7d64;
      --tree-near: #385540;
      --cloud: #f6fbff;
      --star: transparent;
      --aurora-opacity: 0.08;
    }
  }
  &.weather-night.theme-nordic {
    .theme-bg {
      --sky-top: #050b14;
      --sky-bottom: #0d2b36;
      --mtn-far: #16323f;
      --mtn-mid: #102530;
      --mtn-near: #081820;
      --mtn-highlight: #294858;
      --fjord: #0d3545;
      --fjord-shimmer: #6fd7ff;
      --forest-far: #132c22;
      --tree-near: #0b2518;
      --cloud: #1e3b4b;
      --star: #dff8ff;
      --aurora-a: #31d8a0;
      --aurora-b: #586cff;
      --aurora-c: #b5f4dc;
      --aurora-opacity: 0.55;
    }
  }
  &.weather-bright.theme-japan,
  &.weather-sunny.theme-japan {
    .theme-bg {
      --sky-top: #ffc9da;
      --sky-bottom: #8fd5f0;
      --moon: #fff0a8;
      --star: transparent;
      --cloud: #fff5f9;
      --fuji: #638db0;
      --fuji-shadow: #4a7298;
      --ground: #5f895e;
      --village-far: #4a7270;
      --lantern: #ffd56f;
    }
  }
  &.weather-cloudy.theme-japan {
    .theme-bg {
      --sky-top: #b9b3c4;
      --sky-bottom: #abbfca;
      --moon: #e7dcc3;
      --star: transparent;
      --cloud: #d9d7dd;
      --fuji: #667b91;
      --fuji-shadow: #4b6077;
      --ground: #566f58;
      --village-far: #465e63;
      --sakura: #d98faa;
      --sakura-dark: #b8698d;
    }
  }
  &.weather-rainy.theme-japan,
  &.weather-heavy-rain.theme-japan {
    .theme-bg {
      --sky-top: #4c4a63;
      --sky-bottom: #6b7283;
      --moon: #d7cab0;
      --star: transparent;
      --cloud: #777986;
      --fuji: #3f536a;
      --fuji-shadow: #2f4258;
      --ground: #334d3a;
      --village-far: #30454b;
      --building: #bfa994;
      --lantern: #ffbf5a;
      --neon: #48d9ee;
      --sakura: #c4759c;
      --sakura-dark: #914d75;
    }
  }
  &.weather-snow.theme-japan {
    .theme-bg {
      --sky-top: #d8d9e8;
      --sky-bottom: #c7dde9;
      --moon: #fff1bd;
      --star: transparent;
      --cloud: #f1eef5;
      --fuji: #7f96ad;
      --fuji-shadow: #617990;
      --ground: #667c69;
      --village-far: #667b80;
      --snow: #ffffff;
      --building: #ead8c4;
      --sakura: #f0a9c5;
      --sakura-dark: #c77b9e;
    }
  }
  &.weather-night.theme-japan {
    .theme-bg {
      --sky-top: #090822;
      --sky-bottom: #39205b;
      --moon: #ffe79a;
      --star: #fff4b8;
      --cloud: #33264f;
      --fuji: #1d2445;
      --fuji-shadow: #101833;
      --ground: #142818;
      --village-far: #141c2a;
      --building: #302133;
      --lantern: #ffd36c;
      --neon: #59f1ff;
      --sakura: #ff7caf;
      --sakura-dark: #743456;
    }
  }
  &.dark.theme-nordic {
    .theme-bg {
      --sky-top: #050b14;
      --sky-bottom: #0d2b36;
      --mtn-far: #16323f;
      --mtn-mid: #102530;
      --mtn-near: #081820;
      --mtn-highlight: #294858;
      --snow: #dfeaf0;
      --fjord: #0d3545;
      --fjord-shimmer: #6fd7ff;
      --forest-far: #132c22;
      --tree-near: #0b2518;
      --trunk: #2b1c14;
      --cabin: #5c2323;
      --roof: #2c1414;
      --door: #1a0f0a;
      --window: #ffd873;
      --chimney: #130b0d;
      --cloud: #1e3b4b;
      --star: #dff8ff;
      --particle: #ffffff;
      --aurora-a: #31d8a0;
      --aurora-b: #586cff;
      --aurora-c: #b5f4dc;
      --aurora-opacity: 0.55;
    }
  }
  &.dark.theme-japan {
    .theme-bg {
      --sky-top: #090822;
      --sky-bottom: #39205b;
      --moon: #ffe79a;
      --star: #fff4b8;
      --cloud: #33264f;
      --fuji: #1d2445;
      --fuji-shadow: #101833;
      --snow: #dce9ff;
      --ground: #142818;
      --village-far: #141c2a;
      --roof: #471a2d;
      --building: #302133;
      --torii: #b63038;
      --lantern: #ffd36c;
      --neon: #59f1ff;
      --bamboo: #0e2a19;
      --trunk: #251617;
      --sakura: #ff7caf;
      --sakura-dark: #743456;
    }
  }
  &.dark {
    background: var(--dark-bg);
    color: white;
    a,
    button {
      color: white;
      &:hover {
        color: var(--pink);
        .nav-github {
          filter: invert(40%) sepia(94%) saturate(6890%) hue-rotate(329deg)
            brightness(95%) contrast(104%);
        }
      }
    }
    .right-frame {
      .info-card {
        background: rgba(255, 255, 255, 0.04);
        border-color: rgba(255, 255, 255, 0.1);
        box-shadow: 0 16px 34px -18px rgba(0, 0, 0, 0.6);
      }
      .info-row {
        border-bottom-color: rgba(255, 255, 255, 0.09);
        dt {
          color: #23d5ab !important;
        }
      }
      .languages span {
        background: rgba(255, 255, 255, 0.08);
      }
      .documents {
        border-top-color: rgba(255, 255, 255, 0.16);
      }
      .document-item {
        background: rgba(255, 255, 255, 0.05);
        border-color: rgba(255, 255, 255, 0.1);
        &:hover {
          background: rgba(255, 255, 255, 0.12);
        }
      }
    }
    .header-footer .education-item {
      background: rgba(255, 255, 255, 0.04);
      border-color: rgba(255, 255, 255, 0.1);
      box-shadow: 0 16px 34px -18px rgba(0, 0, 0, 0.6);
    }
  }
  .header-footer {
    position: relative;
    z-index: 1;
    flex-shrink: 0;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    padding: 0 30px 20px 30px;
    .education-item {
      display: grid;
      justify-content: center;
      align-items: center;
      grid-template-columns: 110px 1fr;
      background: rgba(255, 255, 255, 0.55);
      backdrop-filter: blur(14px);
      height: 100px;
      -webkit-backdrop-filter: blur(14px);
      border: 1px solid rgba(255, 255, 255, 0.65);
      border-radius: 18px;
      box-shadow: 0 16px 34px -18px rgba(20, 40, 70, 0.35);
      padding: 10px;
      .education-title {
        font-size: 1.3rem;
        line-height: 1.3rem;
        font-weight: 700;
      }
      img {
        width: 7rem;
        &.unisul-logo {
          width: 5rem;
        }
        &.icon-ala {
          filter: invert(42%) sepia(62%) saturate(1799%) hue-rotate(178deg)
            brightness(101%) contrast(101%);
        }
      }
    }
  }
`;
